/**
 * Motion primitives for the walkthrough.
 *
 * Three rules hold here, and they are the same three that hold everywhere else
 * on this site:
 *
 * **Motion is the reader's preference.** Every animation checks
 * `prefers-reduced-motion`, and the reduced path still reaches the same final
 * state. A demo that only works while it animates is a broken demo.
 *
 * **A new run replaces the one in flight.** Changing a control mid-animation
 * must not queue behind it and must not be dropped — either leaves the controls
 * saying one thing while the stage shows another.
 *
 * **Nothing here talks to the network.** The walkthrough is a diagram of the
 * product, not an instance of it: no source, no repository, no API call.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduced(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  return reduced
}

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, value))

export interface RunToken {
  /** False as soon as any later run starts, or the component unmounts. */
  readonly live: boolean
}

/** Returns a `start()` that mints a token for the run about to begin. */
export function useRunner(): () => RunToken {
  const generation = useRef(0)

  useEffect(
    () => () => {
      generation.current += 1
    },
    [],
  )

  return useCallback(() => {
    const mine = ++generation.current
    return {
      get live() {
        return generation.current === mine
      },
    }
  }, [])
}

/** Animates a number toward its target, so a figure changing is visible. */
export function useCounter(target: number, reduced: boolean, ms = 700): number {
  const [value, setValue] = useState(target)
  const from = useRef(target)

  useEffect(() => {
    if (reduced) {
      from.current = target
      setValue(target)
      return undefined
    }
    const start = from.current
    const t0 = performance.now()
    let frame = 0
    const tick = (t: number) => {
      const k = clamp((t - t0) / ms, 0, 1)
      const eased = 1 - (1 - k) ** 3
      setValue(start + (target - start) * eased)
      if (k < 1) frame = requestAnimationFrame(tick)
      else from.current = target
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, reduced, ms])

  return value
}

export interface PacketOptions {
  ms?: number
  className?: string
  radius?: number
  count?: number
  gap?: number
  reduced?: boolean
}

/**
 * Sends dots along an SVG path — the "data moving" primitive the flow maps are
 * built from. Under reduced motion it resolves immediately and draws nothing, so
 * the caller's sequence still completes.
 */
export function sendPacket(
  root: SVGSVGElement | null,
  path: SVGPathElement | null,
  { ms = 900, className = 'pkt', radius = 4, count = 1, gap = 120, reduced = false }: PacketOptions = {},
): Promise<void> {
  if (reduced || !root || !path) return Promise.resolve()

  const length = path.getTotalLength()
  const dots = Array.from({ length: count }, (_, i) => {
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    dot.setAttribute('r', String(radius))
    dot.setAttribute('class', className)
    dot.setAttribute('opacity', '0')
    root.appendChild(dot)
    return { dot, delay: i * gap }
  })

  const t0 = performance.now()
  return new Promise((resolve) => {
    const tick = (t: number) => {
      let live = false
      for (const { dot, delay } of dots) {
        const k = (t - t0 - delay) / ms
        if (k < 0) {
          live = true
          continue
        }
        if (k >= 1) {
          dot.setAttribute('opacity', '0')
          continue
        }
        live = true
        const point = path.getPointAtLength(length * k)
        dot.setAttribute('cx', String(point.x))
        dot.setAttribute('cy', String(point.y))
        dot.setAttribute('opacity', String(Math.min(1, Math.sin(k * Math.PI) * 2.2)))
      }
      if (live) requestAnimationFrame(tick)
      else {
        dots.forEach(({ dot }) => dot.remove())
        resolve()
      }
    }
    requestAnimationFrame(tick)
  })
}

const numberFormat = new Intl.NumberFormat('en-US')

export const formatNumber = (n: number): string => numberFormat.format(Math.round(n))

/** Token counts, at the magnitude they are usually quoted. */
export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}k`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return formatNumber(n)
}
